import { ISimpleUserInfoHolder, T_ISimpleUserInfoHolder } from '@cgs/common';
import {
  BonusRequestParams,
  BuyFeatureRequestParams,
  DATA_ACCESS_ERROR_HANDLER_SERVICE_TOKEN,
  DATA_ACCESS_SERVICE_TOKEN,
  DataAccessErrorHandlerService,
  DataAccessService,
  GambleRequestParams,
  SlotMachineInfo,
  SpinResponse,
  User,
} from '@cgs/data-access';
import { Lazy } from '@cgs/shared';
import { Container } from '@cgs/wge';
import {
  LAUNCH_PARAMS_STORE_TOKEN,
  LaunchParamsStore,
  SLOT_MACHINE_STORE_TOKEN,
  SlotMachineStore,
} from '../store';
import {
  FALLBACK_MACHINE_STATE_SERVICE_TOKEN,
  FallbackMachineStateService,
} from './fallback-machine-state.service';

export const GAME_DATA_SERVICE_TOKEN = Symbol('GAME_DATA_SERVICE_TOKEN');

export class GameDataService<T = unknown> {
  protected readonly launchParamsStore: LaunchParamsStore<T>;
  protected readonly slotMachineStore: SlotMachineStore;

  protected readonly dataAccessService: DataAccessService;
  private readonly simpleUserInfoHolder: ISimpleUserInfoHolder;
  protected readonly errorHandler: DataAccessErrorHandlerService | null;
  protected readonly fallbackMachineStateService: Lazy<FallbackMachineStateService>;

  constructor(private container: Container) {
    this.dataAccessService = this.container.resolve<DataAccessService>(DATA_ACCESS_SERVICE_TOKEN);
    this.simpleUserInfoHolder =
      this.container.resolve<ISimpleUserInfoHolder>(T_ISimpleUserInfoHolder);
    this.launchParamsStore =
      this.container.resolve<LaunchParamsStore<T>>(LAUNCH_PARAMS_STORE_TOKEN);
    this.slotMachineStore = this.container.resolve<SlotMachineStore>(SLOT_MACHINE_STORE_TOKEN);
    this.errorHandler = this.container.resolve<DataAccessErrorHandlerService>(
      DATA_ACCESS_ERROR_HANDLER_SERVICE_TOKEN,
      { optional: true }
    );
    this.fallbackMachineStateService = new Lazy(() =>
      this.container.resolve<FallbackMachineStateService>(FALLBACK_MACHINE_STATE_SERVICE_TOKEN)
    );
  }

  public async loadUser(): Promise<User> {
    try {
      const user = await this.dataAccessService.getUserInfo();
      this.simpleUserInfoHolder.user = user;
      return user;
    } catch (error) {
      this.errorHandler?.handleError(error as Error);
      throw error;
    }
  }

  public async startSlot(): Promise<SlotMachineInfo> {
    try {
      const slotMachineInfo = await this.dataAccessService.start();
      this.updateStartState(slotMachineInfo);

      if (slotMachineInfo.machineState) {
        this.slotMachineStore.state = slotMachineInfo.machineState;
      }
      if (slotMachineInfo.balance !== null) {
        this.updateBalance(slotMachineInfo.balance);
      }

      return slotMachineInfo;
    } catch (error) {
      this.errorHandler?.handleError(error as Error);
      throw error;
    }
  }

  protected updateStartState(slotMachineInfo: SlotMachineInfo): void {
    this.launchParamsStore.update({
      bets: slotMachineInfo.bets,
      defaultBet: slotMachineInfo.defaultBet,
      betMultiplier: slotMachineInfo.betMultiplier,
      maxLines: slotMachineInfo.maxLines,
      symbols: slotMachineInfo.symbols,
      cheatEnabled: slotMachineInfo.cheatUser,
      cheatConfigs: slotMachineInfo.configs,
      cheatCommands: slotMachineInfo.cheatCommands,
      cheatReels: slotMachineInfo.cheatReels,
      customCheatInput: slotMachineInfo.customCheatInput,
    });
  }

  public async spin(): Promise<SpinResponse> {
    try {
      const spinResult = await this.dataAccessService.spin(this.slotMachineStore.state);
      this.slotMachineStore.state = spinResult.machineState;
      this.updateBalance(spinResult.balance);
      return spinResult.machineState;
    } catch (error) {
      this.errorHandler?.handleError(error as Error);
      this.slotMachineStore.error = error as Error;

      this.slotMachineStore.state = this.fallbackMachineStateService.call().getSpinMachineState();
      return this.slotMachineStore.state;
    }
  }

  public async buyFeature(params: BuyFeatureRequestParams): Promise<SpinResponse> {
    try {
      const buyFeatureResult = await this.dataAccessService.buyFeature(params);
      this.slotMachineStore.state = buyFeatureResult.machineState;
      this.updateBalance(buyFeatureResult.balance);
      return buyFeatureResult.machineState;
    } catch (error) {
      this.errorHandler?.handleError(error as Error);
      this.slotMachineStore.error = error as Error;

      this.slotMachineStore.state = this.fallbackMachineStateService
        .call()
        .getBuyFeatureMachineState();
      return this.slotMachineStore.state;
    }
  }

  public async pickBonus(params: BonusRequestParams): Promise<SpinResponse> {
    try {
      const spinResult = await this.dataAccessService.pickBonus(params);
      this.slotMachineStore.state = spinResult.machineState;
      return spinResult.machineState;
    } catch (error) {
      this.errorHandler?.handleError(error as Error);
      this.slotMachineStore.error = error as Error;

      this.slotMachineStore.state = this.fallbackMachineStateService.call().getBonusMachineState();
      return this.slotMachineStore.state;
    }
  }

  public async gamble<Data = any>(params: GambleRequestParams<Data>): Promise<SpinResponse> {
    try {
      const gambleResult = await this.dataAccessService.gamble(params);
      this.slotMachineStore.state = gambleResult.machineState;
      return gambleResult.machineState;
    } catch (error) {
      this.errorHandler?.handleError(error as Error);
      this.slotMachineStore.error = error as Error;

      this.slotMachineStore.state = this.fallbackMachineStateService
        .call()
        .getGamblesMachineState();
      return this.slotMachineStore.state;
    }
  }

  protected updateBalance(newBalance: number): void {
    const user = this.simpleUserInfoHolder.user;

    this.simpleUserInfoHolder.user = new User({
      balance: newBalance,
      userId: user.userId,
      cheatUser: user.cheatUser,
    });
  }
}
